import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { productUrl } from "@/lib/productUrl";
import { Lock } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "just now";
  const t = Date.parse(iso);
  if (isNaN(t)) return "just now";
  const s = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

// A single item pill
function TickerItem({ lock }) {
  const first = lock.first_name || "Someone";
  const school = lock.school_name || "Wisconsin";
  const product = lock.product_name || "a look";
  const to = lock.product_slug && lock.category_slug
    ? productUrl({ category_slug: lock.category_slug, slug: lock.product_slug })
    : "/prom-dresses";
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-3 shrink-0 group"
      data-testid={`ticker-item-${lock.id}`}
    >
      <span className="inline-flex items-center gap-1.5 bg-rosemetal text-ivory px-2.5 py-1 rounded-full font-label text-[9px] tracking-widest">
        <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
        {lock.status === "purchased" ? "PURCHASED" : "LOCKED"}
      </span>
      {lock.image && (
        <span className="w-8 h-10 overflow-hidden bg-blush rounded-sm shrink-0">
          <img src={lock.image} alt="" className="w-full h-full object-cover" />
        </span>
      )}
      <span className="font-body text-sm text-ink whitespace-nowrap">
        <b className="font-display">{first}</b>
        <span className="text-muted-foreground"> at </span>
        <b className="font-display">{school}</b>
        <span className="text-muted-foreground"> → </span>
        <span className="italic">{product}</span>
        {lock.color ? <span className="text-muted-foreground"> · {lock.color}</span> : null}
      </span>
      <span className="font-label text-[9px] text-rosemetal tracking-widest">
        {timeAgo(lock.created_at)}
      </span>
    </Link>
  );
}

export default function JustLockedTicker() {
  const [locks, setLocks] = useState([]);

  useEffect(() => {
    const load = () => api.recentLocks(15).then(({ data }) => setLocks(data || [])).catch(() => {});
    load();
    const t = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(t);
  }, []);

  if (locks.length === 0) return null;

  // Duplicate list twice for a seamless loop
  const loop = [...locks, ...locks];

  return (
    <section data-testid="just-locked-ticker" className="mt-8 border-y border-border bg-blush/30 overflow-hidden py-3 relative">
      {/* Left "LIVE" pill — absolutely positioned so sliding items pass BEHIND it */}
      <div className="absolute left-0 top-0 bottom-0 flex items-center z-30 pl-5 pr-5 bg-gradient-to-r from-ivory via-ivory/95 to-transparent">
        <div className="flex items-center gap-2 shrink-0 bg-ivory px-3 py-1.5 rounded-full border border-cream shadow-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rosemetal opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-rosemetal" />
          </span>
          <span className="font-label text-[10px] text-rosemetal tracking-widest whitespace-nowrap">JUST LOCKED · LIVE</span>
        </div>
      </div>

      {/* Right-edge fade */}
      <div className="absolute right-0 top-0 bottom-0 w-24 z-20 pointer-events-none bg-gradient-to-l from-ivory to-transparent" />

      {/* Sliding track — left-padded so its content starts after the pill */}
      <div className="ticker-track flex items-center gap-10 whitespace-nowrap pl-[220px]">
        {loop.map((l, i) => (
          <TickerItem key={`${l.id}-${i}`} lock={l} />
        ))}
      </div>

      <style>{`
        .ticker-track { animation: ticker-scroll 60s linear infinite; }
        .ticker-track:hover { animation-play-state: paused; }
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}
